import React, {useState, useEffect} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';
import TempChartArea from '../components/TempChartArea.js';
import useCountdownRefresh from '../utils/useCountdownRefresh.js';

const REFRESH_INTERVAL = 60;
const MAX_REFERENCE_ROWS = 2;
const CHART_STATS_ROWS = MAX_REFERENCE_ROWS + 3; // referenční hodnoty + max + min + průměr
const PLACEHOLDER_TS = '2026-01-01T00:00:00';
const GRAPH_PERIODS = [
	{id: '25h', title: 'Posledních 25h', label: '25h', xLabelMode: '25h', showCurrentPoint: true},
	{id: '72h', title: 'Poslední 3 dny', label: '3 dny', xLabelMode: '6h', showCurrentPoint: true},
	{id: 'week', title: 'Poslední týden', label: 'Týden', xLabelMode: 'daily'},
	{id: 'month', title: 'Poslední měsíc', label: 'Měsíc', xLabelMode: '5days'},
	{id: 'year', title: 'Poslední rok', label: 'Rok', xLabelMode: 'monthly'},
];

export default function Temperature({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [sensors, setSensors] = useState([]);
	const [sensorId, setSensorId] = useState(-1);

	const [activeGraphPeriod, setActiveGraphPeriod] = useState('25h');
	const [stats, setStats] = useState(null);
	const [dataGraph25h, setDataGraph25h] = useState(null);
	const [dataGraph72h, setDataGraph72h] = useState(null);
	const [dataGraphWeek, setDataGraphWeek] = useState(null);
	const [dataGraphMonth, setDataGraphMonth] = useState(null);
	const [dataGraphYear, setDataGraphYear] = useState(null);
	const {secondsLeft: counterCnt, restart: restartCountdown} = useCountdownRefresh(REFRESH_INTERVAL, () => loadData(activeGraphPeriod));

	useEffect(() => {
		loadSensors();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (sensorId !== -1) {
			clearGraphData();
			loadData(activeGraphPeriod);
			restartCountdown();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sensorId]);

	const loadSensors = () => {
		setLoading(true);
		post('getTempSensors', {})
			.then((res) => {
				const sensorData = res.data || [];
				setSensors(sensorData);
				if (sensorData.length > 0 && sensorId === -1) {
					setSensorId(sensorData[0].ID);
				}
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const clearGraphData = () => {
		setDataGraph25h(null);
		setDataGraph72h(null);
		setDataGraphWeek(null);
		setDataGraphMonth(null);
		setDataGraphYear(null);
	};

	const getGraphDataByPeriod = (period) => {
		if (period === '72h') return dataGraph72h;
		if (period === 'week') return dataGraphWeek;
		if (period === 'month') return dataGraphMonth;
		if (period === 'year') return dataGraphYear;
		return dataGraph25h;
	};

	const setGraphDataFromResponse = (res) => {
		if (res.data_graph_25h !== undefined) setDataGraph25h(res.data_graph_25h);
		if (res.data_graph_72h !== undefined) setDataGraph72h(res.data_graph_72h);
		if (res.data_graph_week !== undefined) setDataGraphWeek(res.data_graph_week);
		if (res.data_graph_month !== undefined) setDataGraphMonth(res.data_graph_month);
		if (res.data_graph_year !== undefined) setDataGraphYear(res.data_graph_year);
	};

	const loadData = (period = activeGraphPeriod) => {
		if (sensorId === -1) {
			return;
		}

		setLoading(true);
		post('getTempData', {sensorId: sensorId, period: period})
			.then((res) => {
				setStats(res.stats);
				setGraphDataFromResponse(res);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const handleRefresh = () => {
		loadData(activeGraphPeriod);
		restartCountdown();
	};

	const handleGraphPeriodChange = (period) => {
		if (period === activeGraphPeriod) {
			return;
		}

		setActiveGraphPeriod(period);
		restartCountdown();

		if (!getGraphDataByPeriod(period)) {
			loadData(period);
		}
	};

	const accentColor = dataGraph72h?.color || dataGraph25h?.color || '#f97316';
	const activeGraphConfig = GRAPH_PERIODS.find((item) => item.id === activeGraphPeriod) || GRAPH_PERIODS[0];
	const activeGraphData = getGraphDataByPeriod(activeGraphConfig.id);

	const formatDate = (dateStr) => {
		if (!dateStr) return '';
		const date = new Date(dateStr);
		return date.toLocaleDateString('cs-CZ', {day: 'numeric', month: 'numeric', year: 'numeric'}) + ' ' + date.toLocaleTimeString('cs-CZ', {hour: '2-digit', minute: '2-digit', second: '2-digit'});
	};

	const renderTrendArrow = () => {
		const current = stats?.current?.temp;
		const previous = stats?.previous?.temp;
		if (current === null || current === undefined || previous === null || previous === undefined) {
			return null;
		}
		const curNum = parseFloat(current);
		const prevNum = parseFloat(previous);
		if (isNaN(curNum) || isNaN(prevNum)) {
			return null;
		}

		let arrow = '→';
		let color = '#16a34a'; // zelená - stejná teplota
		if (curNum > prevNum) {
			arrow = '↗'; // sikmo nahoru - teplota stoupá
			color = '#dc2626'; // červená
		} else if (curNum < prevNum) {
			arrow = '↘'; // sikmo dolů - teplota klesá
			color = '#2563eb'; // modrá
		}

		return (
			<span className="temperature-page__trend" style={{color: color, fontWeight: 'bold'}}>
				{arrow}
			</span>
		);
	};

	// Řádek statistiky. Placeholder se vykreslí neviditelný, ale se stejným obsahem, aby zabral shodnou výšku (včetně zalomení na mobilu).
	const renderStatsRow = (label, temp, ts, key, placeholder = false) => (
		<div key={key} className={placeholder ? 'temperature-page__stats-row is-placeholder' : 'temperature-page__stats-row'} aria-hidden={placeholder ? 'true' : undefined}>
			<strong className="temperature-page__stats-label">{label}:</strong> {temp ?? '-'} °C {ts !== null ? <span className="temperature-page__stats-meta">({formatDate(ts)})</span> : null}
		</div>
	);

	// Stejná kostra jako sekce s grafem, jen bez dat - drží layout při načítání i u senzoru bez měření
	const renderChartPlaceholderSection = (message) => (
		<section className="temperature-page__chart-section">
			<h2 className="temperature-page__chart-title">{activeGraphConfig.title}</h2>
			<div className="temperature-page__chart-stats is-placeholder" aria-hidden="true">
				{Array.from({length: CHART_STATS_ROWS}, (_, index) => renderStatsRow('Hodnota před 24 h', '00.0', index === CHART_STATS_ROWS - 1 ? null : PLACEHOLDER_TS, `skeleton-${index}`))}
			</div>
			<div className="stat-page__panel stat-page__panel--chart temperature-page__chart-panel temperature-page__chart-panel--empty" style={{'--stat-accent': accentColor}}>
				<div className="stat-page__empty">{message}</div>
			</div>
		</section>
	);

	const renderChartSection = (title, graphData, xLabelMode = null, referencePoints = null, showCurrentPoint = false) => {
		if (!graphData?.data?.[0]?.data?.length) {
			return null;
		}

		const visibleReferencePoints = (referencePoints || []).filter(Boolean);
		// Referenční řádky má jen 25h (1) a 3 dny (2). Chybějící doplníme placeholdery, aby graf zůstal u všech období na stejném místě.
		const referencePlaceholders = Array.from({length: Math.max(0, MAX_REFERENCE_ROWS - visibleReferencePoints.length)}, (_, index) => index);

		return (
			<section className="temperature-page__chart-section" key={title}>
				<h2 className="temperature-page__chart-title">{title}</h2>
				{graphData.stats ? (
					<div className="temperature-page__chart-stats">
						{visibleReferencePoints.map((point) => renderStatsRow(`Hodnota před ${point.label}`, point.temp, point.ts, point.label))}
						{referencePlaceholders.map((index) => renderStatsRow('Hodnota před 24 h', '00.0', PLACEHOLDER_TS, `placeholder-${index}`, true))}
						{renderStatsRow('Maximální teplota', graphData.stats.max?.temp, graphData.stats.max?.ts ?? PLACEHOLDER_TS, 'max')}
						{renderStatsRow('Minimální teplota', graphData.stats.min?.temp, graphData.stats.min?.ts ?? PLACEHOLDER_TS, 'min')}
						{renderStatsRow('Průměrná teplota', graphData.stats.avg?.temp, null, 'avg')}
					</div>
				) : null}
				<div className="stat-page__panel stat-page__panel--chart temperature-page__chart-panel" style={{'--stat-accent': accentColor}}>
					<TempChartArea className="temperature-page__chart" data={graphData.data} labels={graphData.labels} timestamps={graphData.timestamps} ranges={graphData.ranges} color={accentColor} linesColor={'#c8d4ce'} variant="glass" height="100%" xLabelMode={xLabelMode} referencePoints={visibleReferencePoints} showCurrentPoint={showCurrentPoint} />
				</div>
			</section>
		);
	};

	const activeReferencePoints = ['25h', '72h'].includes(activeGraphConfig.id)
		? [
			stats?.reference24h ? {...stats.reference24h, label: '24 h'} : null,
			activeGraphConfig.id === '72h' && stats?.reference48h ? {...stats.reference48h, label: '48 h'} : null,
		]
		: null;
	const hasActiveChartData = activeGraphData?.data?.[0]?.data?.length > 0;

	return (
		<div className="page_main">
			<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />

			<div className={`page temperature-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
				<section className="temperature-page__toolbar">
					{sensors.length > 0 ? (
						<div className="temperature-page__sensor-row">
							{sensorId !== -1 ? (
								<div className="icon_refresh_main home-refresh temperature-page__refresh" onClick={() => handleRefresh()}>
									<div className="icon_refresh_in">{counterCnt}</div>
									<img className="icon_refresh fl_l cr_p mt_01" alt="" />
								</div>
							) : null}
							<div className="temperature-page__sensor-toolbar">
								{sensors.map((sensor) => (
									<button key={sensor.ID} type="button" className={`temperature-page__sensor-chip${sensorId === sensor.ID ? ' is-active' : ''}`} onClick={() => setSensorId(sensor.ID)}>
										<span className="temperature-page__sensor-dot"></span>
										{sensor.NAME}
									</button>
								))}
							</div>
						</div>
					) : null}

					{/* Blok vykreslujeme i před načtením dat (skrytý), aby se obsah pod ním neposouval */}
					<div className={`temperature-page__stats${stats ? '' : ' is-placeholder'}`} aria-hidden={stats ? undefined : 'true'}>
						<div className="temperature-page__stats-row">
							<strong className="temperature-page__stats-label">Aktuální teplota:</strong> {renderTrendArrow()} {stats?.current?.temp ?? '00.0'} °C <span className="temperature-page__stats-meta">({formatDate(stats?.current?.ts ?? PLACEHOLDER_TS)})</span>
						</div>
						<div className="temperature-page__stats-row">
							<strong className="temperature-page__stats-label">Maximální teplota:</strong> {stats?.max?.temp ?? '00.0'} °C <span className="temperature-page__stats-meta">({formatDate(stats?.max?.ts ?? PLACEHOLDER_TS)})</span>
						</div>
						<div className="temperature-page__stats-row">
							<strong className="temperature-page__stats-label">Minimální teplota:</strong> {stats?.min?.temp ?? '00.0'} °C <span className="temperature-page__stats-meta">({formatDate(stats?.min?.ts ?? PLACEHOLDER_TS)})</span>
						</div>
					</div>
				</section>

				<div className="temperature-page__charts">
					<div className="temperature-page__graph-toolbar">
						{GRAPH_PERIODS.map((period) => (
							<button key={period.id} type="button" className={`temperature-page__sensor-chip temperature-page__graph-chip${activeGraphPeriod === period.id ? ' is-active' : ''}`} onClick={() => handleGraphPeriodChange(period.id)}>
								<span className="temperature-page__sensor-dot"></span>
								{period.label}
							</button>
						))}
					</div>

					<div className="temperature-page__charts-list">
						{hasActiveChartData
							? renderChartSection(activeGraphConfig.title, activeGraphData, activeGraphConfig.xLabelMode, activeReferencePoints, activeGraphConfig.showCurrentPoint)
							: renderChartPlaceholderSection(loading ? 'Načítám data...' : 'Pro vybraný senzor nejsou k dispozici žádná data.')}
					</div>
				</div>
			</div>
		</div>
	);
}
